import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, FormControl, FormLabel, Input, Modal, ModalClose, ModalDialog,
    Option, Select, Stack, Typography } from '@mui/joy';
import { AppContext } from '../context';
import { APIGetAdminSalaryCurrent, parseApiError } from '../api';
import { businessTodayISO } from '../businessDay';
import NumericInput from './NumericInput';

const money = value => value == null ? '—' : `${Number(value).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`;

export default function AdminSalaryModal({ open, employee, onClose, onSave, loading, error }) {
    const { token } = useContext(AppContext);
    const [date, setDate] = useState('');
    const [locationId, setLocationId] = useState(null);
    const [value, setValue] = useState('');
    const [current, setCurrent] = useState(null);
    const [fetching, setFetching] = useState(false);
    const [fetchError, setFetchError] = useState('');
    const [saved, setSaved] = useState(false);
    const requestId = useRef(0);
    const employeeId = employee?.id;

    useEffect(() => {
        requestId.current += 1;
        if (open) {
            setDate(businessTodayISO()); setLocationId(null); setCurrent(null);
            setValue(''); setSaved(false); setFetchError('');
        }
        return () => { requestId.current += 1; };
    }, [open, employeeId]);

    const loadCurrent = useCallback(async () => {
        if (!open || !employeeId || !date) return;
        const id = ++requestId.current;
        setFetching(true); setFetchError(''); setCurrent(null); setValue('');
        const result = await APIGetAdminSalaryCurrent(token, {
            administrator_id: employeeId, date, ...(locationId ? { location_id: locationId } : {}),
        });
        if (id !== requestId.current) return;
        setFetching(false);
        if (result?.error || !result) {
            setFetchError(parseApiError(result?.error, 'Не удалось загрузить оклад'));
            return;
        }
        setCurrent(result);
        setValue(result.base_value == null ? '' : String(result.base_value));
    }, [open, employeeId, date, locationId, token]);
    useEffect(() => {
        loadCurrent();
        return () => { requestId.current += 1; };
    }, [loadCurrent]);

    const handleSave = async () => {
        if (!current?.can_edit_base || fetching || loading) return;
        setSaved(false);
        const result = await onSave({ value, date, location_id: current.location_id });
        if (result && !result.error) {
            setSaved(true);
            await loadCurrent();
        }
    };
    const changeScope = () => {
        requestId.current += 1; setCurrent(null); setValue(''); setSaved(false);
    };
    return <Modal open={open} onClose={loading ? undefined : onClose}
        slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', bgcolor: 'rgba(0,0,0,0.65)' } } }}>
        <ModalDialog sx={{ width: 'calc(100vw - 32px)', maxWidth: 420,
            height: 'auto', minHeight: 0, maxHeight: 'calc(100dvh - 32px)',
            top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            bgcolor: 'var(--joy-palette-background-level1)', border: '1px solid var(--joy-palette-divider)',
            borderRadius: 'var(--joy-radius-xl)', p: { xs: 2, sm: 2.5 }, overflowY: 'auto' }}>
            <ModalClose disabled={loading} />
            <Typography level="title-lg" sx={{ pr: 3, color: '#22c55e', fontWeight: 800 }}>Оклад за день</Typography>
            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', overflowWrap: 'anywhere' }}>
                {employee?.full_name || employee?.first_name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>
                Здесь меняется только фикс за выбранный день. Процент от выручки считается отдельно.
            </Typography>
            {(error || fetchError) && <Alert color="danger" variant="soft">{error || fetchError}</Alert>}
            <FormControl>
                <FormLabel>Дата</FormLabel>
                <Input type="date" size="sm" value={date} disabled={loading}
                    onChange={event => { changeScope(); setDate(event.target.value); }} />
            </FormControl>
            {current?.locations?.length > 1 && <FormControl>
                <FormLabel>Точка</FormLabel>
                <Select size="sm" value={locationId || current.location_id} disabled={loading}
                    onChange={(_, id) => { changeScope(); setLocationId(id); }}
                    slotProps={{ listbox: { sx: { bgcolor: 'var(--joy-palette-background-level2)',
                        color: 'var(--joy-palette-text-primary)', border: '1px solid var(--joy-palette-divider)',
                        '& .MuiOption-root': { color: 'var(--joy-palette-text-primary)' } } } }}>
                    {current.locations.map(location => <Option key={location.id} value={location.id}>{location.name}</Option>)}
                </Select>
            </FormControl>}
            {fetching ? <Typography level="body-sm">Загрузка оклада…</Typography> : current && <Box
                sx={{ bgcolor: 'var(--joy-palette-background-level2)', borderRadius: 8, p: 1.25 }}>
                <Stack direction="row" justifyContent="space-between" gap={1}>
                    <Typography level="body-sm">Оклад (фикс)</Typography><Typography level="body-sm" fontWeight={700} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{money(current.base_value)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" gap={1}>
                    <Typography level="body-sm">Процент от выручки</Typography><Typography level="body-sm" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{money(current.revenue_value)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" gap={1} sx={{ mt: 0.5 }}>
                    <Typography level="body-sm">ЗП за день без премий и штрафов</Typography><Typography level="body-sm" fontWeight={700} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{money(current.value)}</Typography>
                </Stack>
                {current.base_is_manual && <Typography level="body-xs" sx={{ mt: 0.5 }}>Фикс задан вручную</Typography>}
            </Box>}
            {current?.explanation && <Alert color="warning" variant="soft">{current.explanation}</Alert>}
            <FormControl>
                <FormLabel>Новый оклад (фикс за день), ₽</FormLabel>
                <NumericInput value={value} inputMode="decimal" decimalPlaces={2} min={0}
                    disabled={fetching || loading || !current?.can_edit_base}
                    onChange={event => { setSaved(false); setValue(event.target.value); }} placeholder="Например: 500" size="sm" />
            </FormControl>
            {saved && <Alert color="success" variant="soft">Оклад за день сохранён</Alert>}
            <Button onClick={handleSave} loading={loading}
                disabled={fetching || !current?.can_edit_base || value === '' || !Number.isFinite(Number(value)) || Number(value) < 0 || !date}
                color="success" sx={{ fontWeight: 800 }}>Сохранить оклад за день</Button>
        </ModalDialog>
    </Modal>;
}
